import React, { FC, useEffect, useState } from "react"
import "../styles/smartlink.css"
import { Release, SmartLink, SmartLinkUrl } from "../../../../types"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { releases } from "../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { toast } from "react-toastify"
import { getSocialMediaIcon, mapToCamelCase } from "../../../../utility"
import { SmartLinkPages } from "../ReleaseSmartLinkPage"

// @ts-ignore
import KahunaLogo from "../../../../assets/logo-icons/kahuna.png"

//@ts-ignore
import SpotifyIcon from "../../../../assets/release-platform-icons/SpotifyIcon.png"
import { lang } from "../../../../constants/languages"

type ConnectPageProps = {
  release: Release
  setSelectedPage: (selectedPage: number) => void
}

const ConnectPage: FC<ConnectPageProps> = (props) => {
  const { release, setSelectedPage } = props

  const [autoGenerate, setAutoGenerate] = useState(false)
  const [preSaveEnabled, setPreSaveEnabled] = useState(false)
  const [smartLink, setSmartLink] = useState<SmartLink>({})
  const [addNew, setAddNew] = useState<boolean>(false)
  const [newLinkText, setNewLinkText] = useState("")

  const fillFavIcons = (smartLink: SmartLink) => {
    const smartLinkUrls =
      smartLink?.urls?.map((smartLinkUrl: SmartLinkUrl) => {
        const baseUrl = smartLinkUrl.url?.replace("https://", "").replace("http://", "").split("/")[0]
        return { ...smartLinkUrl, icon: `https://${baseUrl}/favicon.ico` }
      }) || []
    setSmartLink({ ...smartLink, urls: smartLinkUrls })
  }

  const fetchSmartLink = () => {
    releases(`/release/smart_links/?release_id=${release.id}`, "get").then((resp) => {
      if (resp && resp.data && resp.data.result) {
        fillFavIcons(mapToCamelCase(resp.data.result))
        setAutoGenerate(resp.data.auto_generate)
        setPreSaveEnabled(resp.data.pre_save_enabled)
      }
    })
  }

  useEffect(() => {
    fetchSmartLink()
  }, [])

  const removeSmartLinkUrl = (id?: number) => {
    if (!id) {
      return
    }

    releases(`/release/remove_smart_link_url/?id=${id}`, "delete").then((resp) => {
      if (resp && resp.data && resp.data.success) {
        const urls = [...(smartLink.urls || [])].filter((url) => url.id !== id)
        fillFavIcons({ ...smartLink, urls })

        toast.success(lang.catalog.smart_link.success.smart_link_remove, successFilledToastOptions)
      } else {
        toast.error(lang.catalog.smart_link.error.error_on_smart_link_remove, errorFilledToastOptions)
      }
    })
  }

  const saveSmartLink = () => {
    releases(`/release/save_smart_links/`, "post", {
      smart_link_id: smartLink.id,
      url: newLinkText
    }).then((resp) => {
      if (resp && resp.data && resp.data.result) {
        const smartLinkUrl = mapToCamelCase(resp.data.result)
        const urls = [...(smartLink.urls || []), smartLinkUrl]
        fillFavIcons({ ...smartLink, urls })

        setNewLinkText("")
        setAddNew(false)
        toast.success(lang.catalog.smart_link.success.smart_link_save, successFilledToastOptions)
      } else {
        toast.error(lang.catalog.smart_link.error.error_on_smart_link_save, errorFilledToastOptions)
      }
    })
  }

  const renderIcon = (url: string, icon: string) => {
    const socialMediaIcon = getSocialMediaIcon(url)
    const platformIcon = socialMediaIcon || icon
    const padding = url.includes("www.youtube.com") ? 2 : 6
    return (
      <img
        src={platformIcon}
        style={{ padding, width: 32, height: 32 }}
        onError={(e: any) => {
          e.target.src = KahunaLogo
          e.target.style.padding = 0
        }}
      />
    )
  }

  const renderUrls = () => {
    return (
      <div style={{ width: "100%" }}>
        {smartLink.urls?.map((smartLinkUrl) => {
          return (
            <div key={smartLink.id} className="mt-2">
              {autoGenerate && smartLinkUrl.url?.includes("spotify") ? (
                <div className="relative">
                  <span className="auto-generated-span absolute mt-3.5 mr-4" style={{ right: 0 }}>
                    KahunaAI created
                  </span>
                </div>
              ) : (
                <div className="relative">
                  <img
                    onClick={() => removeSmartLinkUrl(smartLinkUrl.id)}
                    className="cursor-pointer absolute mt-3 mr-4"
                    style={{ right: 0 }}
                    src="/account_icons/trash_red.svg"
                  />
                </div>
              )}
              <div
                className="flex"
                style={{
                  borderRadius: 10,
                  padding: 6,
                  border: "1px solid var(--stroke-soft-200, #F3F3F3)",
                  boxShadow: "0px 1px 2px 0px rgba(228, 229, 231, 0.24)"
                }}
              >
                {smartLinkUrl.url?.includes("spotify") ? (
                  <img src={SpotifyIcon} style={{ padding: 4, width: 32, height: 32 }} />
                ) : (
                  renderIcon(smartLinkUrl.url || "", smartLinkUrl.icon || "")
                )}

                <div
                  className="items-center grid cursor-pointer ml-2 mr-10"
                  style={{ width: "100%" }}
                  onClick={() => {
                    window.open(smartLinkUrl?.url || "", "_blank")
                  }}
                >
                  <KSpan text={smartLinkUrl?.url || ""} color="#000" fontWeight={400} />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderSmartLinkAdd = () => {
    return !addNew ? (
      <div className="mt-6 flex gap-2">
        <KButton
          gap="2px"
          width="auto"
          padding="0px"
          text={lang.catalog.smart_link.add_link_button}
          leftIcon="/navigation_icons/plus.svg"
          onClick={() => setAddNew(true)}
          background="transparent"
          shadowDisabled={true}
        />
      </div>
    ) : (
      <div className="mt-6" style={{ width: "100%" }}>
        <div
          style={{ width: "100%" }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && newLinkText && addNew) {
              saveSmartLink()
            }
          }}
        >
          <KInput
            gap="12px"
            padding="12px"
            leftIcon="/account_icons/link.svg"
            onChange={(value) => setNewLinkText(value)}
            value={newLinkText}
            placeholder={lang.catalog.smart_link.new_link_placeholder}
          />
          <div className="relative">
            <img
              className="cursor-pointer absolute mr-4"
              src="/account_icons/trash_red.svg"
              style={{ right: 0, marginTop: -33 }}
              onClick={() => setAddNew(false)}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minWidth: 616 }}>
      <div style={{ maxHeight: "58vh", overflowY: "scroll" }}>
        <div>
          <KTitleSpan text={lang.catalog.smart_link.connect_title} fontSize={32} />
        </div>
        <div className="mt-6 flex gap-2">{renderUrls()}</div>
      </div>
      <div>
        {preSaveEnabled ? (
          <KSpan text={lang.catalog.smart_link.pre_save_enabled} />
        ) : (
          renderSmartLinkAdd()
        )}
        <div className="mt-4">
          <KButton
            background="black"
            textColor="white"
            text={lang.catalog.smart_link.continue_button}
            onClick={() => setSelectedPage(SmartLinkPages.ANALYTICS)}
            shadowDisabled={true}
          />
        </div>
      </div>
    </div>
  )
}

export default ConnectPage
